
import debounce from 'lodash/debounce';

import { StateListenerRegistry, equals } from '../base/redux';
import { isFollowMeActive } from '../follow-me';

import { setRemoteParticipantsWithScreenShare } from './actions';
import { getCurrentLayout } from './functions';
import { getActivePresentationSignature, orchestratePriorityContent } from './presentationStack';

declare var interfaceConfig: Object;


/**
 * For auto-pin mode, listen for changes to the known media tracks and look
 * for updates to screen shares. The listener is debounced to avoid state
 * thrashing that might occur, especially when switching in or out of p2p.
 */
StateListenerRegistry.register(
    /* selector */ state => state['features/base/tracks'],
    /* listener */ debounce((tracks, store) => {
        if (!_getAutoPinSetting() || isFollowMeActive(store)) {
            return;
        }

        const oldScreenSharesOrder = store.getState()['features/video-layout'].remoteScreenShares || [];
        const knownSharingParticipantIds = tracks.reduce((acc, track) => {
            if (track.mediaType === 'screenshare' && track.videoType === 'desktop' && track.muted === false) {
                const skipTrack = _getAutoPinSetting() === 'remote-only' && track.local;

                if (!skipTrack) {
                    // For screenshare tracks, use the source name as the participant ID
                    // This matches the fake participant ID created for screenshares
                    const participantId = track.mhTrack?.getSourceName() || track.participantId;

                    acc.push(participantId);
                }
            }

            return acc;
        }, []);

        // Filter out any participants which are no longer screen sharing
        // by looping through the known sharing participants and removing any
        // participant IDs which are no longer sharing.
        const newScreenSharesOrder = oldScreenSharesOrder.filter(
            participantId => knownSharingParticipantIds.includes(participantId));

        // Make sure all new sharing participant get added to the end of the
        // known screen shares.
        knownSharingParticipantIds.forEach(participantId => {
            if (!newScreenSharesOrder.includes(participantId)) {
                newScreenSharesOrder.push(participantId);
            }
        });

        if (!equals(oldScreenSharesOrder, newScreenSharesOrder)) {
            store.dispatch(
                setRemoteParticipantsWithScreenShare(newScreenSharesOrder));
        }
    }, 100));

/**
 * The unified Layout & Priority Orchestrator subscriber. Runs whenever the set/order
 * of active presentation items (screen share, shared video, whiteboard, etherpad)
 * changes, maintaining the LIFO priority content stack and coordinating the auto-pin
 * and large-video election. This is the single, centralized place that owns those
 * decisions, and it is shared by Web and React Native.
 */
StateListenerRegistry.register(
    /* selector */ state => getActivePresentationSignature(state),
    /* listener */ (signature, store) => orchestratePriorityContent(store)
);

/**
 * Re-run the orchestrator whenever the layout changes. The orchestrator suppresses
 * auto-pinning while in tile view, so when the layout transitions OUT of tile view
 * (for example a recorder switching from tile view to stage/vertical-filmstrip once
 * a screen share starts, or a user manually leaving tile view while a presentation is
 * active) we must re-evaluate and pin the topmost active presentation. Without this,
 * the pin decision taken while still in tile view (a no-op) would never be revisited
 * and the large video would elect a camera instead of the active screen share.
 */
StateListenerRegistry.register(
    /* selector */ state => getCurrentLayout(state),
    /* listener */ (layout, store) => orchestratePriorityContent(store)
);

/**
 * A selector for retrieving the current automatic pinning setting.
 *
 * @private
 * @returns {string|undefined} The string "remote-only" is returned if only
 * remote screensharing should be automatically pinned, any other truthy value
 * means automatically pin all screenshares. Falsy means do not automatically
 * pin any screenshares.
 */
function _getAutoPinSetting() {
    return typeof interfaceConfig === 'object'
        ? interfaceConfig.AUTO_PIN_LATEST_SCREEN_SHARE
        : 'remote-only';
}

